import get from 'lodash/get';

import bluebird from 'utils/bluebird';
import { trimSearch } from '@xmatters/common/utils/searchUtil';
import { getListingURIGenerator } from '@xmatters/redux/actions/listPage/utils';

const uri = '/webapi/xm/1/dynamic-teams';

export const SEARCH_DYNAMIC_TEAMS_LIST_PAGE = 'SEARCH_DYNAMIC_TEAM';
export const SEARCH_DYNAMIC_TEAMS_LIST_PAGE_START = `${SEARCH_DYNAMIC_TEAMS_LIST_PAGE}_START`;
export const SEARCH_DYNAMIC_TEAMS_LIST_PAGE_SUCCESS = `${SEARCH_DYNAMIC_TEAMS_LIST_PAGE}_SUCCESS`;
export const SEARCH_DYNAMIC_TEAMS_LIST_PAGE_FAIL = `${SEARCH_DYNAMIC_TEAMS_LIST_PAGE}_FAIL`;

/**
 * Get all the filter selections
 * @param {object} props
 * @param {string} props.searchText
 * @param {object} props.supervisors object containing an array with selected supervisors
 * @returns {object} returns object with all filter selections
 */
export const getSearchFilterParams = props => {
  const search = get(props, 'search');
  const supervisors = get(props, 'supervisors.value');

  const params = {};

  if (search) {
    params.search = search;
    params.operand = 'AND';
    params.fields = ['NAME', 'DESCRIPTION'];
  }
  if (supervisors) {
    params.supervisors = supervisors;
  }

  return params;
};

/**
 * Fetch dynamic teams  based on filter and sorting selections
 * @param {object} props
 * @param {string} props.searchText
 * @param {string} props.sortBy: ASCENDING, DESCENDING
 * @param {number} props.startIndex
 * @param {number} props.stopIndex
 * @param {object} props.supervisors object containing an array with selected supervisors
 * @returns {object} a Redux action
 */
export const searchDynamicTeams = (props = {}) => {
  const { startIndex = 0, stopIndex = 0, sortBy, sortOrder } = props;

  const params = {
    limit: Math.max(100, stopIndex - startIndex),
    offset: startIndex,
    sortOrder: sortOrder ? sortOrder.toUpperCase() : undefined,
    sortBy: sortBy ? sortBy.toUpperCase() : undefined,
    embed: 'permittedActions',
    ...getSearchFilterParams(props),
  };

  return {
    type: SEARCH_DYNAMIC_TEAMS_LIST_PAGE,
    meta: {
      xmApi: true,
      method: 'get',
      uri,
      params,
      customUriWithParamsGenerator: getListingURIGenerator,
    },
  };
};

export const DYNAMIC_TEAMS_LIST_PAGE_SEARCH_USERS =
  'DYNAMIC_TEAMS_LIST_PAGE_SEARCH_USERS';
export const DYNAMIC_TEAMS_LIST_PAGE_SEARCH_USERS_START = `${DYNAMIC_TEAMS_LIST_PAGE_SEARCH_USERS}_START`;
export const DYNAMIC_TEAMS_LIST_PAGE_SEARCH_USERS_SUCCESS = `${DYNAMIC_TEAMS_LIST_PAGE_SEARCH_USERS}_SUCCESS`;
export const DYNAMIC_TEAMS_LIST_PAGE_SEARCH_USERS_FAIL = `${DYNAMIC_TEAMS_LIST_PAGE_SEARCH_USERS}_FAIL`;

/**
 * Fetch users which match the search string
 * @param {string} search
 * @returns {object} a Redux action
 */
export const getUsersBySearch = search => {
  const usersUri = '/webapi/xm/1/people';
  const params = {};

  const trimmedSearchText = trimSearch(search);
  if (trimmedSearchText.length >= 2) {
    params.search = trimmedSearchText;
    params.operand = 'AND';
    params.fields = ['FIRST_NAME', 'LAST_NAME', 'TARGET_NAME'];
  }

  return {
    type: DYNAMIC_TEAMS_LIST_PAGE_SEARCH_USERS,
    meta: {
      xmApi: true,
      method: 'get',
      uri: usersUri,
      params,
    },
  };
};

export const DYNAMIC_TEAMS_LIST_PAGE_HYDRATE_ACTIVE_USERS =
  'DYNAMIC_TEAMS_LIST_PAGE_HYDRATE_ACTIVE_USERS';
export const DYNAMIC_TEAMS_LIST_PAGE_HYDRATE_ACTIVE_USERS_START = `${DYNAMIC_TEAMS_LIST_PAGE_HYDRATE_ACTIVE_USERS}_START`;
export const DYNAMIC_TEAMS_LIST_PAGE_HYDRATE_ACTIVE_USERS_SUCCESS = `${DYNAMIC_TEAMS_LIST_PAGE_HYDRATE_ACTIVE_USERS}_SUCCESS`;
export const DYNAMIC_TEAMS_LIST_PAGE_HYDRATE_ACTIVE_USERS_FAIL = `${DYNAMIC_TEAMS_LIST_PAGE_HYDRATE_ACTIVE_USERS}_FAIL`;

/**
 * Get all the selected supervisors
 * @param {array} activeUserIds
 * @returns {object} a Redux action
 */
export const hydrateActiveUsers = activeUserIds => {
  const groupsUri = '/webapi/xm/1/people';
  const params = {};

  if (activeUserIds) {
    params.id = activeUserIds;
  }

  return {
    type: DYNAMIC_TEAMS_LIST_PAGE_HYDRATE_ACTIVE_USERS,
    meta: {
      xmApi: true,
      method: 'get',
      uri: groupsUri,
      params,
    },
  };
};

export const DYNAMIC_TEAMS_LIST_PAGE_RESET_USER_FILTERS =
  'DYNAMIC_TEAMS_LIST_PAGE_RESET_USERS_FILTERS';
export const DYNAMIC_TEAMS_LIST_PAGE_ACTIVE_USERS_FILTERS =
  'DYNAMIC_TEAMS_LIST_PAGE_ACTIVE_USERS_FILTERS';
export const setActiveUserFilters = users => ({
  type: DYNAMIC_TEAMS_LIST_PAGE_ACTIVE_USERS_FILTERS,
  payload: users,
});
export const resetUserFilters = users => ({
  type: DYNAMIC_TEAMS_LIST_PAGE_RESET_USER_FILTERS,
  payload: users,
});

export const DYNAMIC_TEAMS_LIST_PAGE_SET_LOADING =
  'DYNAMIC_TEAMS_LIST_PAGE_SET_LOADING';
export const setIsLoading = () => ({
  type: DYNAMIC_TEAMS_LIST_PAGE_SET_LOADING,
});

/**
 * Delete single dynamic team
 * @param {string} groupUuid : can also pass the team target name
 * @returns {object} a Redux action
 */
export const deleteDynamicTeam = dynamicTeamUuid => ({
  meta: {
    xmApi: true,
    method: 'del',
    uri: `${uri}/${dynamicTeamUuid}`,
  },
});

export const DYNAMIC_TEAMS_LIST_PAGE_DELETE_GROUPS =
  'DYNAMIC_TEAMS_LIST_PAGE_DELETE_GROUPS';
export const DYNAMIC_TEAMS_LIST_PAGE_DELETE_GROUPS_START = `${DYNAMIC_TEAMS_LIST_PAGE_DELETE_GROUPS}_START`;
export const DYNAMIC_TEAMS_LIST_PAGE_DELETE_GROUPS_SUCCESS = `${DYNAMIC_TEAMS_LIST_PAGE_DELETE_GROUPS}_SUCCESS`;
export const DYNAMIC_TEAMS_LIST_PAGE_DELETE_GROUPS_FAIL = `${DYNAMIC_TEAMS_LIST_PAGE_DELETE_GROUPS}_FAIL`;

/**
 * To delete all the selected dynamic teams
 * @param {array} groupUuids
 * @returns {function}
 */
export const deleteDynamicTeams = dynamicTeamUuids => dispatch => {
  const onSuccess = () =>
    dispatch({ type: DYNAMIC_TEAMS_LIST_PAGE_DELETE_GROUPS_SUCCESS });
  const onFail = () =>
    dispatch({ type: DYNAMIC_TEAMS_LIST_PAGE_DELETE_GROUPS_FAIL });

  dispatch({ type: DYNAMIC_TEAMS_LIST_PAGE_DELETE_GROUPS_START });
  return bluebird.mapSeries(dynamicTeamUuids, uuid =>
    dispatch(deleteDynamicTeam(uuid)).then(onSuccess).catch(onFail),
  );
};
